import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Radio, TestTube2, AlertTriangle, Loader2, Send, ShieldCheck, Info,
  Building2, CheckCircle2,
} from 'lucide-react';

export default function BroadcastPanel({ templates = [] }) {
  const [templateId, setTemplateId] = useState('');
  const [monthLabel, setMonthLabel] = useState('');
  const [deadline, setDeadline] = useState('');
  const [batchSize, setBatchSize] = useState(500);
  const [dryLoading, setDryLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [dryResult, setDryResult] = useState(null);

  const [clinics, setClinics] = useState([]);
  const [selectedClinics, setSelectedClinics] = useState(new Set());
  const [clinicSearch, setClinicSearch] = useState('');

  const [diagTo, setDiagTo] = useState('919971925446');
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagResult, setDiagResult] = useState(null);

  const currentTpl = useMemo(() => templates.find(t => t.template_id === templateId) || null, [templates, templateId]);
  const tplParamCount = (currentTpl?.parameters || []).length;

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/whatsapp/broadcast/clinic-summary');
        setClinics(r.data.data || []);
      } catch { /* silent */ }
    })();
  }, []);

  useEffect(() => { setDryResult(null); }, [templateId, batchSize, selectedClinics]);

  const filteredClinics = useMemo(
    () => clinics.filter(c => !clinicSearch || (c.clinic_name || '').toLowerCase().includes(clinicSearch.toLowerCase())),
    [clinics, clinicSearch]
  );

  const selectedReach = useMemo(() => {
    if (selectedClinics.size === 0) return clinics.reduce((a, c) => a + (c.reachable || 0), 0);
    return clinics.filter(c => selectedClinics.has(c.clinic_id)).reduce((a, c) => a + (c.reachable || 0), 0);
  }, [clinics, selectedClinics]);

  const toggleClinic = (id) => {
    const next = new Set(selectedClinics);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedClinics(next);
  };
  const selectAll = () => setSelectedClinics(new Set(clinics.map(c => c.clinic_id)));
  const clearAll = () => setSelectedClinics(new Set());

  const broadcastPayload = () => ({
    template_id: templateId,
    month_label: monthLabel,
    deadline,
    batch_size: batchSize,
    clinic_ids: selectedClinics.size > 0 ? Array.from(selectedClinics) : undefined,
  });

  const runDry = async () => {
    if (!templateId) { toast.error('Pick a template first'); return; }
    setDryLoading(true);
    try {
      const r = await api.post('/whatsapp/broadcast/non-sold', { ...broadcastPayload(), dry_run: true });
      setDryResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Dry-run failed');
    } finally { setDryLoading(false); }
  };

  const runLive = async () => {
    if (!templateId) return;
    const scope = selectedClinics.size > 0 ? `${selectedClinics.size} clinic${selectedClinics.size === 1 ? '' : 's'}` : 'ALL clinics';
    if (!window.confirm(`Send template "${templateId}" to ${dryResult?.would_send || batchSize} patients across ${scope}. Continue?`)) return;
    setSendLoading(true);
    try {
      const r = await api.post('/whatsapp/broadcast/non-sold', { ...broadcastPayload(), dry_run: false });
      toast.success(`Broadcast sent: ${r.data.sent} · Failed: ${r.data.failed}`);
      setDryResult({ ...dryResult, last_send: r.data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Broadcast failed');
    } finally { setSendLoading(false); }
  };

  const runDiag = async () => {
    if (!templateId || !diagTo) { toast.error('Need a template + a phone number'); return; }
    setDiagLoading(true);
    try {
      const r = await api.post('/whatsapp/diagnostic-send', {
        template_id: templateId, to: diagTo,
        parameters: tplParamCount > 0 ? { '0': 'Test', '1': monthLabel || 'August', '2': deadline, '3': '9505540400' } : {},
      });
      setDiagResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Diagnostic failed');
    } finally { setDiagLoading(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="broadcast-panel">
      {/* --- BROADCAST --- */}
      <Card className="p-4 border-stone-200">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 rounded bg-indigo-100 flex items-center justify-center shrink-0">
            <Radio className="w-4 h-4 text-indigo-700" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-stone-900">Broadcast — non-sold patients</h3>
            <p className="text-xs text-stone-500">Every non-sold patient with a mobile. Always dry-run first.</p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Template</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger className="h-9" data-testid="broadcast-tpl-select"><SelectValue placeholder="Choose a Karix template…" /></SelectTrigger>
              <SelectContent>
                {templates.map(t => (
                  <SelectItem key={t.template_id} value={t.template_id}>
                    {t.name} <span className="text-stone-400">({(t.parameters || []).length} params)</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {currentTpl && (
              <div className="text-[11px] text-stone-500 mt-1 leading-relaxed">
                <span className="font-mono">{currentTpl.template_id}</span> · <b>{tplParamCount}</b> parameter{tplParamCount === 1 ? '' : 's'}.
                {tplParamCount === 0 && (
                  <span className="text-amber-700 ml-1">Static template — parameters will be ignored.</span>
                )}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs flex items-center gap-1"><Building2 className="w-3 h-3" /> Clinics ({selectedClinics.size === 0 ? 'ALL' : selectedClinics.size} selected · {selectedReach.toLocaleString()} reach)</Label>
              <div className="flex gap-1.5">
                <button type="button" onClick={selectAll} className="text-[10px] uppercase tracking-widest text-indigo-700 hover:underline" data-testid="broadcast-clinics-all">All</button>
                <span className="text-stone-300 text-[10px]">|</span>
                <button type="button" onClick={clearAll} className="text-[10px] uppercase tracking-widest text-stone-500 hover:underline" data-testid="broadcast-clinics-none">Clear</button>
              </div>
            </div>
            <Input value={clinicSearch} onChange={e => setClinicSearch(e.target.value)}
              placeholder="Search clinic…" className="h-8 mb-1.5" data-testid="broadcast-clinic-search" />
            <ScrollArea className="h-36 border rounded-md bg-white">
              <div className="p-1.5 space-y-0.5">
                {filteredClinics.length === 0 && (
                  <div className="text-[11px] text-stone-400 p-2">No clinics match.</div>
                )}
                {filteredClinics.map(c => (
                  <label key={c.clinic_id} className="flex items-center gap-2 px-1.5 py-1 hover:bg-stone-50 rounded cursor-pointer text-[11px]" data-testid={`broadcast-clinic-${c.clinic_id}`}>
                    <Checkbox checked={selectedClinics.has(c.clinic_id)} onCheckedChange={() => toggleClinic(c.clinic_id)} />
                    <span className="flex-1 truncate">{c.clinic_name}</span>
                    <span className="tabular-nums text-stone-500">{c.reachable.toLocaleString()}</span>
                  </label>
                ))}
              </div>
            </ScrollArea>
            <div className="text-[10px] text-stone-500 mt-1">
              {selectedClinics.size === 0
                ? 'No clinics selected → broadcast will target ALL clinics.'
                : 'Broadcast will only target the checked clinics.'}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Fallback Month <span className="text-stone-400">(auto per patient)</span></Label>
              <Input value={monthLabel} onChange={e => setMonthLabel(e.target.value)}
                placeholder="e.g. August" className="h-9" data-testid="broadcast-month" disabled={tplParamCount === 0} />
            </div>
            <div>
              <Label className="text-xs">Offer deadline</Label>
              <Input value={deadline} onChange={e => setDeadline(e.target.value)}
                placeholder="e.g. 15th August" className="h-9" data-testid="broadcast-deadline" disabled={tplParamCount === 0} />
            </div>
          </div>

          <div>
            <Label className="text-xs">Batch size (safety cap)</Label>
            <Input type="number" min={1} max={40000} value={batchSize}
              onChange={e => setBatchSize(Math.max(1, Math.min(40000, Number(e.target.value) || 500)))}
              className="h-9 w-40" data-testid="broadcast-batch" />
            <p className="text-[10px] text-stone-500 mt-1">Max 40,000 per call. Recommend ≤500/batch, ≤2 batches/hour to stay within Meta tier limits.</p>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={runDry} disabled={dryLoading || !templateId}
              data-testid="broadcast-dry-btn" className="h-9">
              {dryLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />}
              Dry-run
            </Button>
            <Button onClick={runLive} disabled={sendLoading || !dryResult}
              data-testid="broadcast-live-btn"
              className="h-9 bg-indigo-600 hover:bg-indigo-700">
              {sendLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
              Send batch
            </Button>
          </div>

          {dryResult && (
            <div className="border rounded-md p-3 bg-stone-50 space-y-2" data-testid="broadcast-dry-result">
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <Badge variant="outline" className="text-[10px] uppercase">Dry-run</Badge>
                <span><b>{dryResult.would_send}</b> would send</span>
                <span className="text-stone-400">·</span>
                <span><b>{dryResult.total_universe}</b> total reachable</span>
                {dryResult.template_param_count !== undefined && (
                  <>
                    <span className="text-stone-400">·</span>
                    <span>Template accepts <b>{dryResult.template_param_count}</b> params</span>
                  </>
                )}
              </div>
              {Array.isArray(dryResult.sample_first_5) && dryResult.sample_first_5.length > 0 && (
                <div className="text-[11px] text-stone-600">
                  <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1">First 5 recipients</div>
                  {dryResult.sample_first_5.map((s, i) => (
                    <div key={i} className="font-mono flex items-center gap-2">
                      <span className="text-stone-400">{i + 1}.</span>
                      <span>{s.name || '—'}</span>
                      <span className="text-stone-400">·</span>
                      <span>{s.mobile}</span>
                      <span className="text-stone-400">·</span>
                      <span>{s.clinic}</span>
                      {s.month && <><span className="text-stone-400">·</span><span className="text-indigo-700">{s.month}</span></>}
                    </div>
                  ))}
                </div>
              )}
              {dryResult.last_send && (
                <Alert className="border-emerald-200 bg-emerald-50">
                  <AlertDescription className="text-xs">
                    Live send done · Sent <b>{dryResult.last_send.sent}</b> · Failed <b>{dryResult.last_send.failed}</b>.
                    Check the History tab for per-message status.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* --- DIAGNOSTIC --- */}
      <Card className="p-4 border-stone-200">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 rounded bg-amber-100 flex items-center justify-center shrink-0">
            <TestTube2 className="w-4 h-4 text-amber-700" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-stone-900">Diagnostic — probe one number</h3>
            <p className="text-xs text-stone-500">See the raw Karix response + delivery hints when a send silently drops.</p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Recipient (E.164, e.g. 919971925446)</Label>
            <Input value={diagTo} onChange={e => setDiagTo(e.target.value)}
              placeholder="919…" className="h-9 font-mono" data-testid="diag-to" />
          </div>
          <Button onClick={runDiag} disabled={diagLoading || !templateId || !diagTo}
            data-testid="diag-btn" className="h-9 bg-amber-600 hover:bg-amber-700 text-white">
            {diagLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <TestTube2 className="w-3.5 h-3.5 mr-1.5" />}
            Send probe
          </Button>

          {diagResult && (
            <div className="border rounded-md p-3 bg-stone-50 space-y-2" data-testid="diag-result">
              <div className="flex items-center gap-2">
                <Badge className={diagResult.raw?.success ? 'bg-emerald-600' : 'bg-red-600'}>
                  {diagResult.raw?.success ? 'HTTP OK' : 'FAILED'}
                </Badge>
                <span className="text-xs text-stone-600">HTTP {diagResult.raw?.http}</span>
              </div>
              <Textarea readOnly value={JSON.stringify(diagResult.raw?.response, null, 2)}
                className="text-[10px] font-mono h-28 bg-white" data-testid="diag-raw" />
              {Array.isArray(diagResult.hints) && diagResult.hints.length > 0 && (
                <div className="space-y-1">
                  <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold flex items-center gap-1">
                    <Info className="w-3 h-3" /> Interpretation
                  </div>
                  {diagResult.hints.map((h, i) => (
                    <div key={i} className="text-[11px] text-stone-700 flex gap-1.5">
                      <AlertTriangle className="w-3 h-3 mt-0.5 text-amber-500 shrink-0" />
                      <span>{h}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
